import { useQuery } from '@tanstack/react-query';
import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { Project } from '../data/databaseSchema';
import { employeeService } from '../services/employeeService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { createEmployeeRoleDisplayLookup } from '../utils/employeeDisplay';
import { ProjectDrawer } from './ProjectDrawer';
import { ProjectRolesPanel } from './ProjectRolesPanel';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

function projectDateSortValue(value: unknown): number {
  if (value === null || value === undefined || value === '') return Number.POSITIVE_INFINITY;
  const s = String(value).trim();
  if (s === '') return Number.POSITIVE_INFINITY;
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') {
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(5, 7));
    const day = Number(s.slice(8, 10));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) {
      return Number.POSITIVE_INFINITY;
    }
    return Date.UTC(y, m - 1, day);
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function compareProjectDates(a: unknown, b: unknown): number {
  return projectDateSortValue(a) - projectDateSortValue(b);
}

interface ProjectListProps {
  projects: Project[];
  onEdit?: (project: Project) => void;
  onArchive?: (projectId: string) => void;
  onRestore?: (projectId: string) => void;
  onDeleteProject?: (projectId: string) => void;
  onAdd?: () => void;
  isLoading?: boolean;
}

export const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  onEdit,
  onArchive,
  onRestore,
  onDeleteProject,
  onAdd,
  isLoading = false,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [rolesSheetProject, setRolesSheetProject] = useState<Project | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: employeeService.getEmployees,
    staleTime: 1000 * 60 * 5,
  });
  const formatRoleHolder = useMemo(() => createEmployeeRoleDisplayLookup(employees), [employees]);

  const handleAddClick = useCallback(() => {
    setRolesSheetProject(null);
    setEditingProject(null);
    setPanelMode('add');
  }, []);

  const handleDrawerSave = useCallback(
    (project: Project) => {
      if (onEdit && panelMode === 'edit') {
        onEdit(project);
      } else if (onAdd && panelMode === 'add') {
        onAdd();
      }
    },
    [onEdit, onAdd, panelMode]
  );

  const handleFormClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingProject(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingProject(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedProject(null);
    setEditingProject(null);
  }, []);

  const handleRowClicked = useCallback((event: RowClickedEvent<Project>) => {
    const target = event.event?.target;
    const isCellButtonClick = target instanceof Element && target.closest('button') !== null;
    if (isCellButtonClick) return;
    if (!event.data) return;
    setRolesSheetProject(null);
    setSelectedProject(event.data);
    setPanelMode('selected');
    setEditingProject(null);
  }, []);

  const ManagerCellRenderer = (params: any) => {
    const { data } = params;
    const label = formatRoleHolder(data?.manager);
    return (
      <Tooltip>
        <TooltipTrigger
          className="inline-flex"
          render={
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-auto min-h-0 w-auto shrink-0 p-2 font-normal leading-tight"
              onClick={() => {
                setPanelMode('empty');
                setSelectedProject(null);
                setRolesSheetProject(data);
              }}
            />
          }
        >
          {label}
        </TooltipTrigger>
        <TooltipContent>Click for more role details</TooltipContent>
      </Tooltip>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Number',
        field: 'project_number',
        width: 120,
        pinned: 'left',
        lockPosition: 'left',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: unknown, b: unknown) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return params.value;
        },
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 180,
        maxWidth: 200,
        pinned: 'left',
        lockPosition: 'left',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: unknown, b: unknown) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          const isArchived = params.data?.status === 'archived';
          const displayValue = params.value || 'N/A';
          return (
            <div className="flex items-center gap-2">
              <span title={displayValue}>{displayValue}</span>
              {isArchived && (
                <span className="rounded px-2 py-0.5 text-xs font-semibold uppercase bg-muted text-text-secondary">
                  Archived
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: 'Typ',
        field: 'type',
        width: 80,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        comparator: (a: unknown, b: unknown) => {
          const sa = a == null ? '' : String(a);
          const sb = b == null ? '' : String(b);
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: any) => {
          const t = String(params.value ?? '')
            .trim()
            .toLowerCase();
          if (t === 'internal') {
            return (
              <span
                title="Internal"
                className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-primary-500/20 text-primary-700"
              >
                Int
              </span>
            );
          }
          if (t === 'external') {
            return (
              <span
                title="External"
                className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-warning-500/25 text-text-primary"
              >
                Ext
              </span>
            );
          }
          const raw =
            params.value == null || String(params.value).trim() === ''
              ? 'N/A'
              : String(params.value).trim();
          return <span>{raw}</span>;
        },
      },
      {
        headerName: 'Manager',
        field: 'manager',
        minWidth: 180,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: `${agGridCSSClasses.modernCell} manager-role-cell`,
        cellRenderer: ManagerCellRenderer,
        valueFormatter: (params: any) => formatRoleHolder(params.value),
      },
      {
        headerName: 'Assistant',
        field: 'assistant',
        hide: true,
        minWidth: 150,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatRoleHolder(params.value),
      },
      {
        headerName: 'Deputy',
        field: 'deputy',
        hide: true,
        minWidth: 150,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatRoleHolder(params.value),
      },
      {
        headerName: 'Finance',
        field: 'finance',
        hide: true,
        minWidth: 150,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatRoleHolder(params.value),
      },
      {
        headerName: 'Portfolio',
        field: 'portfolio',
        minWidth: 180,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return params.value;
        },
      },
      {
        headerName: 'Process Tech',
        field: 'process_technology',
        minWidth: 120,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return params.value;
        },
      },
      {
        headerName: 'Delivery Model',
        field: 'delivery_model',
        minWidth: 140,
        flex: 1,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return params.value;
        },
      },
      {
        headerName: 'Start',
        field: 'start_date',
        minWidth: 150,
        width: 160,
        sortable: true,
        comparator: compareProjectDates,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatDateDdMmYyyy(params.value),
      },
      {
        headerName: 'End',
        field: 'end_date',
        minWidth: 150,
        width: 160,
        sortable: true,
        comparator: compareProjectDates,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatDateDdMmYyyy(params.value),
      },
    ],
    [formatRoleHolder]
  );

  // Search functionality
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (gridApi) {
        gridApi.setGridOption('quickFilterText', searchValue);
      }
    },
    [gridApi]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
    }
  }, [gridApi]);

  // Export functionality
  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'projects.csv',
        columnKeys: [
          'id',
          'name',
          'project_number',
          'type',
          'manager',
          'assistant',
          'deputy',
          'finance',
          'portfolio',
          'process_technology',
          'delivery_model',
        ],
      });
    }
  }, [gridApi]);

  // Grid event handlers
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    // Data rendered
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    // Row data updated
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedProjects(selectedData);
    }
  }, [gridApi]);

  // Grid options
  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onRowDataUpdated: handleRowDataUpdated,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    rowNumbers: { width: 40, minWidth: 35 },
  };

  const detailOpen = panelMode !== 'empty' || !!rolesSheetProject;

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        rolesSheetProject ? (
          <ProjectRolesPanel
            project={rolesSheetProject}
            onClose={() => setRolesSheetProject(null)}
            onSaved={(updated) => {
              onEdit?.(updated);
              setRolesSheetProject(updated);
              if (selectedProject?.id === updated.id) {
                setSelectedProject(updated);
              }
            }}
          />
        ) : panelMode === 'edit' || panelMode === 'add' ? (
          <ProjectDrawer
            isOpen={true}
            onClose={handleFormClose}
            onSave={handleDrawerSave}
            onEntrySaved={() => {
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedProject(null);
              onAdd?.();
            }}
            project={editingProject}
            isEdit={panelMode === 'edit'}
          />
        ) : panelMode === 'selected' && selectedProject ? (
          <ProjectDrawer
            key={selectedProject.id}
            isOpen={true}
            readOnly
            project={selectedProject}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={() => {
              setEditingProject(selectedProject);
              setPanelMode('edit');
            }}
            onRestoreRequest={
              selectedProject.status === 'archived' && onRestore
                ? () => onRestore(selectedProject.id)
                : undefined
            }
            onArchiveRequest={
              selectedProject.status !== 'archived' && onArchive
                ? () => onArchive(selectedProject.id)
                : undefined
            }
            onDeleteRequest={
              onDeleteProject
                ? () => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this project? This action cannot be undone.'
                      )
                    ) {
                      onDeleteProject(selectedProject.id);
                      handleViewClose();
                    }
                  }
                : undefined
            }
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col p-2">
          <div className="mb-5 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-3 ">
              <div className="relative  bg-white">
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-[250px] pr-8"
                />
                {searchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </div>

              {selectedProjects.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600">
                  {selectedProjects.length} selected
                </div>
              )}

              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>

            <Button type="button" onClick={handleAddClick}>
              <PlusIcon data-icon="inline-start" />
              Add Project
            </Button>
          </div>

          {!isLoading && projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">📁</div>
              <div className="text-lg font-medium text-text-secondary">No projects found</div>
              <div className="text-sm text-text-muted">Add your first project to get started</div>
              <Button type="button" className="mt-2" onClick={handleAddClick}>
                <PlusIcon data-icon="inline-start" />
                Add Project
              </Button>
            </div>
          ) : (
            <>
              <style>{`${agGridThemeStyles}
.project-list-grid .manager-role-cell,
.project-list-grid .manager-role-cell .ag-cell-wrapper,
.project-list-grid .manager-role-cell .ag-cell-value {
  display: flex;
  align-items: center;
}
.project-list-grid.ag-theme-quartz .ag-header-cell .ag-icon {
  display: inherit !important;
}
.project-list-grid.ag-theme-quartz .ag-header-cell .ag-header-cell-menu-button {
  display: inherit !important;
}
.project-list-grid.ag-theme-quartz .ag-header-cell-sortable {
  cursor: pointer !important;
}
.project-list-grid.ag-theme-quartz .ag-header-cell-sortable:hover {
  cursor: pointer !important;
}
`}</style>
              <div
                className="ag-theme-material project-list-grid min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={projects}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                />
                {isLoading && (
                  <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg bg-surface-card px-8 py-4 text-sm font-medium text-text-secondary shadow-md">
                    <Spinner className="size-4" />
                    Loading projects...
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      }
    />
  );
};
